import { FC, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import useGetTokenData from 'hooks/api/queries/useGetTokenData';
import type { ITokenData } from 'typings/tokenData';
import type { IFolder } from 'typings/document';
import { OrganizationStage } from 'typings/enums';
import Filter from 'components/Filter';
import { convertToHumanDate, generateDateItems } from 'utils/date';
import selectedDateState from 'atoms/selectedDate';

import Folder from './folder';

const VaultAccess: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const { t: translate } = useTranslation();

  return (
    <Stack
      bgcolor="primary.main"
      width={1}
      p={1}
      borderRadius="0.5rem !important"
    >
      <Stack direction={{ xs: 'column', md: 'row' }} p={3}>
        <Box minWidth={0.45} display="flex">
          <Typography
            variant="h4"
            color="common.white"
            fontFamily="Montserrat"
            fontWeight={900}
            sx={{
              textTransform: 'uppercase',
              whiteSpace: 'pre-line',
            }}
          >
            {translate('Vault.Access')}
          </Typography>
        </Box>
        <Stack flexGrow={1} pt={[2, undefined, 0]}>
          <Typography
            variant="body1"
            fontWeight="bold"
            color="common.white"
            pb={2}
          >
            {translate('Common.Welcome')}
          </Typography>

          <Typography variant="body2" color="common.white" lineHeight={1.25}>
            {translate('Vault.Explanation1', {
              appContext: process.env.REACT_APP_VAULT_CONTEXT,
            })}
          </Typography>

          <Typography variant="caption" color="secondary.main" pt={2}>
            {translate('Vault.Explanation2', {
              appContext: process.env.REACT_APP_VAULT_CONTEXT,
            })}
          </Typography>
        </Stack>
      </Stack>

      {isLoading && (
        <Stack
          direction="row"
          width={[1, undefined, undefined, 0.55]}
          ml={[undefined, undefined, undefined, 'auto']}
          pl={3}
          py={2}
          pr={[3, undefined, 8]}
          bgcolor="secondary.main"
          borderRadius="0.3125rem"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="h5"
            color="primary.main"
            fontFamily="Montserrat"
            fontWeight="bold"
            position="relative"
            height="fit-content"
          >
            {translate('Common.LoadingVault')}
          </Typography>

          <CircularProgress />
        </Stack>
      )}
    </Stack>
  );
};

const SkeletonFolder: FC = () => (
  <Paper
    component={Stack}
    elevation={0}
    width={1}
    p={2}
    borderRadius="0.5rem !important"
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Skeleton
        sx={{ height: '2rem', width: [0.5, undefined, undefined, 0.2] }}
      />

      <Skeleton
        sx={{ height: '2.5rem', width: '6rem', borderRadius: '1rem' }}
      />
    </Stack>

    <Skeleton
      sx={{ height: '1rem', width: [0.8, undefined, undefined, 0.4] }}
    />

    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      pt={0.5}
    >
      <Skeleton
        sx={{
          height: '6rem',
          width: [1, undefined, 0.498],
          borderRadius: '0.5rem',
        }}
      />
      <Skeleton
        sx={{
          height: '6rem',
          width: [1, undefined, 0.498],
          borderRadius: '0.5rem',
        }}
      />
    </Stack>
  </Paper>
);

const ErrorModal: FC<{ data?: ITokenData }> = ({ data }) => {
  const { t: translate } = useTranslation();

  return (
    <Dialog
      open
      aria-labelledby="error-dialog-title"
      PaperProps={{
        sx: {
          p: [1, undefined, undefined, 2],
          maxWidth: 450,
          minWidth: [undefined, undefined, undefined, 0.28],
          minHeight: [undefined, undefined, undefined, 300],
        },
      }}
      disableEscapeKeyDown
    >
      <DialogTitle id="error-dialog-title">
        {translate('Common.Welcome')} {data?.organizationName}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="welcome-dialog-description"
          variant="body1"
          color="text.secondary"
          fontFamily="Inter !important"
          fontStyle="normal !important"
          fontWeight="bold"
        >
          {translate('Common.VaultIssue')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          LinkComponent="a"
          href={`mailto:${process.env.REACT_APP_CONTACT_SUPPORT_EMAIL}`}
          sx={{
            width: 0.7,
            py: 2,
            borderRadius: '0.5rem',
            textAlign: 'center',
          }}
        >
          {translate('Common.ContactSupport')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const numberOfSkeletoonFolders = 3;

const skeletons = new Array(numberOfSkeletoonFolders).fill(0);

const Vault: FC = () => {
  const { t: translate, i18n } = useTranslation();
  const [refIndex, setRefIndex] = useState<number>(0);
  const [docTypeIndex, setDocTypeIndex] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useRecoilState(selectedDateState);

  const {
    data,
    isLoading,
    isIdle,
    error: tokenError,
    isFetching,
    isPreviousData,
  } = useGetTokenData({ enabled: true, keepPreviousData: true }, selectedDate);

  const hasError =
    !!tokenError ||
    ((data?.referents?.length ?? 0) === 0 && !isLoading && !isIdle);
  const shouldShowLoaders = isLoading || hasError;
  const shouldShowSkeletons =
    shouldShowLoaders || (isFetching && isPreviousData);
  const folderItems = [
    { name: translate('Common.All') },
    ...(data?.referents?.[refIndex]?.folders ?? []),
  ] as IFolder[];

  const isOngoing =
    data?.referents?.[refIndex]?.ref === OrganizationStage.ongoing;

  const dateItems = useMemo(
    () =>
      data?.fromDate
        ? generateDateItems(
            { fromDate: data.fromDate },
            i18n.language
          ).reverse()
        : [],
    [data?.fromDate, i18n.language]
  );

  return (
    <>
      <Stack py={2}>
        <VaultAccess isLoading={shouldShowLoaders} />

        <Stack direction="row" width={1} justifyContent="flex-end" pt={2}>
          {(data?.referents?.length ?? 0) > 0 && (
            <>
              {isOngoing && (
                <Stack pr={0.5} width={[1 / 3, undefined, undefined, 1 / 4]}>
                  <Filter
                    value={selectedDate}
                    onChange={(event) => {
                      setSelectedDate(event.target.value);
                    }}
                    items={dateItems}
                    renderValue={(selectedDateItem) => (
                      <>
                        <Typography
                          component="span"
                          variant="body1"
                          color="common.grey"
                          fontWeight="bold"
                        >
                          {translate('Common.YearMonth')}:&nbsp;
                        </Typography>

                        <Typography
                          component="span"
                          variant="body1"
                          color="text.primary"
                          fontWeight="bold"
                        >
                          {convertToHumanDate(
                            new Date(`${selectedDateItem as string}-01`)
                          )}
                        </Typography>
                      </>
                    )}
                    renderItem={(dateItem) => (
                      <MenuItem key={`${dateItem.iso}`} value={dateItem.iso}>
                        <Typography
                          component="span"
                          variant="body1"
                          color="text.primary"
                          fontWeight="bold"
                        >
                          {dateItem.human}
                        </Typography>
                      </MenuItem>
                    )}
                  />
                </Stack>
              )}

              <Stack
                pl={isOngoing ? 0.25 : 0.5}
                pr={isOngoing ? 0.25 : undefined}
                width={[1 / 3, undefined, undefined, 1 / 4]}
              >
                <Filter
                  value={docTypeIndex}
                  onChange={(event) =>
                    setDocTypeIndex(Number(event.target.value))
                  }
                  items={folderItems}
                  renderValue={(folderIndex) => (
                    <>
                      <Typography
                        component="span"
                        variant="body1"
                        color="common.grey"
                        fontWeight="bold"
                      >
                        {translate('Common.DocumentType')}:&nbsp;
                      </Typography>

                      <Typography
                        component="span"
                        variant="body1"
                        color="text.primary"
                        fontWeight="bold"
                      >
                        {folderItems[folderIndex as number].name as string}
                      </Typography>
                    </>
                  )}
                  renderItem={(folder, index) => (
                    <MenuItem key={folder.id ?? 0} value={index}>
                      <Typography
                        component="span"
                        variant="body1"
                        color="text.primary"
                        fontWeight="bold"
                      >
                        {folder.name}
                      </Typography>
                    </MenuItem>
                  )}
                />
              </Stack>

              <Stack pl={0.5} width={[1 / 3, undefined, undefined, 1 / 4]}>
                <Filter
                  value={refIndex}
                  onChange={(event) => {
                    setDocTypeIndex(0);
                    setRefIndex(Number(event.target.value));
                  }}
                  items={data?.referents ?? []}
                  renderValue={(referentIndex) => (
                    <>
                      <Typography
                        component="span"
                        variant="body1"
                        color="common.grey"
                        fontWeight="bold"
                      >
                        {translate('Common.Reference')}:&nbsp;
                      </Typography>

                      <Typography
                        component="span"
                        variant="body1"
                        color="text.primary"
                        fontWeight="bold"
                      >
                        {
                          data?.referents?.[referentIndex as number]
                            ?.ref as string
                        }
                      </Typography>
                    </>
                  )}
                  renderItem={(referent, index) => (
                    <MenuItem key={referent.ref} value={index}>
                      <Typography
                        component="span"
                        variant="body1"
                        color="text.primary"
                        fontWeight="bold"
                      >
                        {referent.ref}
                      </Typography>
                    </MenuItem>
                  )}
                />
              </Stack>
            </>
          )}
        </Stack>

        {shouldShowSkeletons ? (
          <>
            {skeletons.map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box pt={2} key={index}>
                <SkeletonFolder />
              </Box>
            ))}
          </>
        ) : (
          (docTypeIndex === 0
            ? data?.referents[refIndex]?.folders
            : [
                data?.referents[refIndex]?.folders?.[
                  docTypeIndex - 1
                ] as IFolder,
              ]
          )?.map((folder) => (
            <Box key={folder.id} pt={2}>
              <Folder
                folder={folder}
                reference={data?.referents[refIndex]?.ref as string}
                refIndex={refIndex}
              />
            </Box>
          ))
        )}
      </Stack>

      {hasError && <ErrorModal />}
    </>
  );
};

export default Vault;
